import React from 'react';
import {
  DatePickerInputField,
  InputField,
  CheckboxField,
  PhoneInputField,
  RadioField,
  TextareaField,
} from '../../../../components/FormikFields';
import MultipleFileUpload from '../../../../components/FormikFields/MultipleFileUpload/MultipleFileUpload';
import {
  ACCEPTED_FILE_TYPES,
  ACTING_AS,
  AGREEMENT_TYPE,
  MAX_FILES_COUNT,
  MAX_SIZE_IN_MB,
  THIRD_PARTY_REGISTRATION,
} from '../../config/constants';

const getToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const getAgreementEndMinDate = ({ agreementType, agreementStartDate }) => {
  const today = getToday();

  if (!agreementStartDate) {
    return today;
  }

  const startDate = new Date(agreementStartDate);
  startDate.setHours(0, 0, 0, 0);

  if (
    agreementType === AGREEMENT_TYPE.THIRD &&
    startDate.getTime() < today.getTime()
  ) {
    return today;
  }

  return startDate;
};

export const addNewBillingAgreementFormConfig = ({
  t,
  lang,
  setFieldValue,
  setTouched,
  values,
  createAs,
  agreementType,
}) => {
  const isThirdPartyAgreement = agreementType === AGREEMENT_TYPE.THIRD;
  const isSellerCreatingThirdPartyAgreement =
    isThirdPartyAgreement && createAs === ACTING_AS.SELLER;
  const isThirdPartyCreatingAgreement =
    isThirdPartyAgreement && createAs === ACTING_AS.THIRD;
  const showThirdPartyVatField = isThirdPartyAgreement
    ? isSellerCreatingThirdPartyAgreement
      ? values.thirdPartyRegistered === THIRD_PARTY_REGISTRATION.VAT_REGISTERED
      : Boolean(values.thirdPartyVatNumber) || !values.thirdPartyTinNumber
    : false;
  const showThirdPartyTinField = isThirdPartyAgreement
    ? isSellerCreatingThirdPartyAgreement
      ? values.thirdPartyRegistered ===
        THIRD_PARTY_REGISTRATION.NOT_VAT_REGISTERED
      : !showThirdPartyVatField
    : false;

  const sellerSection = {
    title: t('special_billing.seller-details'),
    fields: [
      {
        name: 'sellerName',
        label: t('special_billing.name'),
        component: InputField,
        componentProps: {
          placeholder: t('special_billing.please-add-the-seller-name-here'),
        },
      },
      {
        name: 'sellerPhoneNumber',
        label: t('special_billing.phone-number'),
        component: PhoneInputField,
        componentProps: {
          placeholder: t('special_billing.enter-mobile-number-here'),
        },
        required: true,
      },
      {
        name: 'sellerVatNumber',
        label: t('special_billing.vat-account-number'),
        component: InputField,
        componentProps: {
          disabled: createAs === ACTING_AS.SELLER,
          placeholder: t(
            'special_billing.please-add-the-vat-account-number-here',
          ),
        },
      },
      {
        name: 'sellerEmail',
        label: t('special_billing.email-id'),
        component: InputField,
        componentProps: {
          placeholder: t('special_billing.please-add-the-email-id-here'),
        },
        required: true,
      },
    ],
  };

  const buyerSection =
    agreementType === AGREEMENT_TYPE.SELF
      ? {
          title: t('special_billing.buyer-details'),
          fields: [
            {
              name: 'buyerName',
              label: t('special_billing.name'),
              component: InputField,
              componentProps: {
                placeholder: t(
                  'special_billing.please-add-the-buyer-name-here',
                ),
                disabled: createAs === ACTING_AS.BUYER,
              },
              required: true,
            },
            {
              name: 'buyerVatNumber',
              label: t('special_billing.vat-account-number'),
              component: InputField,
              componentProps: {
                placeholder: t(
                  'special_billing.please-add-the-vat-account-number-here',
                ),
                disabled: createAs === ACTING_AS.BUYER,
              },
              required: true,
            },
            {
              name: 'buyerPhoneNumber',
              label: t('special_billing.phone-number'),
              component: PhoneInputField,
              componentProps: {
                placeholder: t('special_billing.enter-mobile-number-here'),
              },
              required: true,
            },
            {
              name: 'buyerEmail',
              label: t('special_billing.email-id'),
              component: InputField,
              componentProps: {
                placeholder: t('special_billing.please-add-the-email-id-here'),
              },
              required: true,
            },
          ],
        }
      : null;

  const thirdPartySection =
    agreementType === AGREEMENT_TYPE.THIRD
      ? {
          title: t('special_billing.third-details'),
          fields: [
            isSellerCreatingThirdPartyAgreement && {
              name: 'thirdPartyRegistered',
              label: t('special_billing.is_third_vat_reg'),
              component: RadioField,
              componentProps: {
                options: [
                  {
                    id: 'third-party-registered-yes',
                    value: THIRD_PARTY_REGISTRATION.VAT_REGISTERED,
                    label: t('common.yes'),
                  },
                  {
                    id: 'third-party-registered-no',
                    value: THIRD_PARTY_REGISTRATION.NOT_VAT_REGISTERED,
                    label: t('common.no'),
                  },
                ],
              },
              required: true,
            },
            {
              name: 'thirdPartyName',
              label: t('special_billing.name'),
              component: InputField,
              componentProps: {
                placeholder: t(
                  'special_billing.please-add-the-third-name-here',
                ),
              },
              required: true,
            },
            {
              name: 'thirdPartyTinNumber',
              label: t('special_billing.tin-number'),
              hide: !showThirdPartyTinField,
              component: InputField,
              componentProps: {
                placeholder: t(
                  'special_billing.please-add-the-tin-number-here',
                ),
                disabled: isThirdPartyCreatingAgreement,
              },
              required: showThirdPartyTinField,
            },
            {
              name: 'thirdPartyVatNumber',
              label: t('special_billing.vat-account-number'),
              hide: !showThirdPartyVatField,
              component: InputField,
              componentProps: {
                placeholder: t(
                  'special_billing.please-add-the-vat-account-number-here',
                ),
                disabled: isThirdPartyCreatingAgreement,
              },
              required: showThirdPartyVatField,
            },
            {
              name: 'thirdPartyPhoneNumber',
              label: t('special_billing.phone-number'),
              component: PhoneInputField,
              componentProps: {
                placeholder: t('special_billing.enter-mobile-number-here'),
              },
              required: true,
            },
            {
              name: 'thirdPartyEmail',
              label: t('special_billing.email-id'),
              component: InputField,
              componentProps: {
                placeholder: t('special_billing.please-add-the-email-id-here'),
              },
              required: true,
            },
            {
              name: 'poaDocument',
              label: t('special_billing.upload-the-poa-document'),
              component: MultipleFileUpload,
              componentProps: {
                downloadFileLabel: t('special_billing.download_agreement'),
                fileTypeErrorMessage: t(
                  'special_billing.agreement_document_file_type_validation',
                ),
                acceptedFileTypes: ACCEPTED_FILE_TYPES,
                placeholder: t(
                  'special_billing.please-upload-the-poa-document-here',
                ),
                maxFileSizeInMB: MAX_SIZE_IN_MB,
                maximumFiles: MAX_FILES_COUNT,
                values,
                setFieldValue,
                setTouched,
              },
            },
          ].filter(Boolean),
        }
      : null;

  const agreementDetailsSection = {
    title:
      agreementType === AGREEMENT_TYPE.SELF
        ? t('special_billing.self-billing-agreement-details')
        : t('special_billing.third-party-agreement-details'),
    fields: [
      {
        name: 'agreementStartDate',
        label: t('special_billing.agreement-start-date'),
        component: DatePickerInputField,
        componentProps: {
          className: 'w-100',
          placeholder: t('special_billing.start-date-placeholder'),
          minDate: isThirdPartyAgreement ? undefined : getToday(),
          dateFormat: 'YYYY-MM-DD',
          displayFormat: 'dd M yy',
        },
        required: true,
      },
      {
        name: 'agreementEndDate',
        label: t('special_billing.agreement-end-date'),
        component: DatePickerInputField,
        componentProps: {
          className: 'w-100',
          dateFormat: 'YYYY-MM-DD',
          displayFormat: 'dd M yy',
          placeholder: t('special_billing.end-date-placeholder'),
          minDate: getAgreementEndMinDate({
            agreementType,
            agreementStartDate: values.agreementStartDate,
          }),
          disabled: !values.agreementStartDate,
        },
        required: true,
      },
      {
        name: 'agreementDocument',
        label:
          agreementType === AGREEMENT_TYPE.SELF
            ? t('special_billing.upload-the-self-billing-agreement')
            : t('special_billing.upload-the-third-party-billing-agreement'),
        tooltip:
          agreementType === AGREEMENT_TYPE.SELF
            ? t('special_billing.upload-self-billing-agreement-tooltip')
            : t('special_billing.upload-third-party-billing-agreement-tooltip'),
        component: MultipleFileUpload,
        componentProps: {
          downloadFileLabel: t('special_billing.download_agreement'),
          fileTypeErrorMessage: t(
            'special_billing.agreement_document_file_type_validation',
          ),
          acceptedFileTypes: ACCEPTED_FILE_TYPES,
          placeholder:
            agreementType === AGREEMENT_TYPE.SELF
              ? t(
                  'special_billing.please-upload-the-self-billing-agreement-here',
                )
              : t(
                  'special_billing.please-upload-the-third-party-billing-agreement-here',
                ),
          maxFileSizeInMB: MAX_SIZE_IN_MB,
          maximumFiles: MAX_FILES_COUNT,
          values,
          setFieldValue,
          setTouched,
        },
        required: true,
      },
      {
        name: 'note',
        label: t('special_billing.note'),
        tooltip: 'special_billing.note-tooltip',
        component: TextareaField,
        componentProps: {
          placeholder: t('special_billing.add-your-note-here'),
          maxlength: 500,
        },
      },
    ],
  };

  if (agreementType === AGREEMENT_TYPE.THIRD) {
    return [
      ...(createAs === ACTING_AS.THIRD
        ? [thirdPartySection, sellerSection]
        : [sellerSection, thirdPartySection]),
      agreementDetailsSection,
    ].filter(Boolean);
  }

  return [sellerSection, buyerSection, agreementDetailsSection].filter(Boolean);
};

export const termsAndConditionsFieldConfig = ({
  t,
  lang,
  setFieldValue,
  setTouched,
  values,
  agreementType,
}) => ({
  name: 'termsAndConditions',
  component: CheckboxField,
  componentProps: {
    label: (
      <div>
        {agreementType == AGREEMENT_TYPE.SELF
          ? t('special_billing.supplier_acknowledge')
          : t('special_billing.supplier_acknowledge_third_party')}
      </div>
    ),
  },
  setFieldValue,
  setTouched,
  required: true,
});
